// notification.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface Notification {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title?: string;
  message: string;
  duration?: number;
  icon?: string;
  backgroundColor?: string;
  color?: string;
  action?: {
    label: string;
    callback: () => void;
  };
  closable?: boolean;
}

@Injectable({
  providedIn: 'root' 
})
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();
  private idCounter = 0;

  success(message: string, title?: string, duration: number = 5000): void {

    this.showNotification('success', message, title, duration);
  }

  error(message: string, title?: string, duration: number = 6000): void {
    this.showNotification('error', message, title, duration);
  }

  info(message: string, title?: string, duration: number = 4000): void {
    this.showNotification('info', message, title, duration);
  }

  warning(message: string, title?: string, duration: number = 5000): void {
    this.showNotification('warning', message, title, duration);
  }


  loading(message: string, title?: string): string {
      const notification: Notification = {
        id: this.generateId(),
        type: 'info',
        title: title,
        message: message,
        duration: 0, // 0 = no se cierra automáticamente
        closable: false // No permitir cerrar manualmente
      };
      this.addNotification(notification);
      return notification.id;
    }
  
    // ✅ NUEVO: Cerrar una notificación específica por ID
    close(id: string): void {
      this.remove(id);
    }
  
    // ✅ NUEVO: Cerrar la última notificación (útil para loading)
    closeLast(): void {
      const current = this.notificationsSubject.value;
      if (current.length > 0) {
        const lastId = current[current.length - 1].id;
        this.remove(lastId);
      }
    }

  showWithAction(
    message: string,
    actionLabel: string,
    actionCallback: () => void,
    type: 'success' | 'error' | 'info' | 'warning' = 'info',
    title?: string,
    duration: number = 0
  ): void {
    const notification: Notification = {
      id: this.generateId(),
      type,
      title,
      message,
      duration,
      action: {
        label: actionLabel,
        callback: actionCallback
      },
      closable: true
    };
    this.addNotification(notification);
  }

  remove(id: string): void {
    const current = this.notificationsSubject.value;
    this.notificationsSubject.next(current.filter(n => n.id !== id));
  }

  clear(): void {
    this.notificationsSubject.next([]);
  }

  private showNotification(
    type: Notification['type'],
    message: string,
    title?: string,
    duration: number = 5000
  ): void {
    const notification: Notification = {
      id: this.generateId(),
      type,
      title,
      message,
      duration,
      closable: true
    };
  
    this.addNotification(notification);
  }

  private addNotification(notification: Notification): void {
    const current = this.notificationsSubject.value;
    this.notificationsSubject.next([...current, notification]);

    if (notification.duration && notification.duration > 0) {
      setTimeout(() => {
        this.remove(notification.id);
      }, notification.duration);
    }
  }

  private generateId(): string {
    return `notification-${++this.idCounter}-${Date.now()}`;
  }
}